/**
 * Gantt task node — a horizontal bar positioned/sized by date, with a progress
 * fill and status colour, or a diamond when it's a milestone. The label sits
 * inside the bar when wide enough, otherwise just to its right.
 */

import { memo } from 'react'
import { NodeProps, Handle, Position } from '@xyflow/react'
import type { GanttContent } from '../../types/schema'

interface TaskData {
  label: string
  ganttContent?: GanttContent
}

const STATUS: Record<string, { bar: string; fill: string; text: string }> = {
  done: { bar: '#e2e8f0', fill: '#94a3b8', text: '#334155' },
  active: { bar: '#bfdbfe', fill: '#3b82f6', text: '#1e3a8a' },
  crit: { bar: '#fecaca', fill: '#ef4444', text: '#7f1d1d' },
  normal: { bar: '#cfe3fb', fill: '#60a5fa', text: '#1e3a8a' },
}

function GanttTaskNode({ data, width, height }: NodeProps) {
  const nodeData = data as unknown as TaskData
  const c = nodeData.ganttContent
  const w = typeof width === 'number' ? width : 60
  const h = typeof height === 'number' ? height : 20
  const name = c?.taskName ?? nodeData.label ?? ''
  const st = STATUS[c?.status ?? 'normal'] ?? STATUS.normal

  if (c?.isMilestone) {
    return (
      <div style={{ width: w, height: h, position: 'relative', overflow: 'visible' }}>
        <Handle type="target" id="gantt-tgt" position={Position.Left} style={{ opacity: 0 }} isConnectable={false} />
        <div style={{ width: h, height: h, background: st.fill, transform: 'rotate(45deg)', borderRadius: 2, boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
        <div style={{ position: 'absolute', left: h + 6, top: 0, height: h, display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', fontSize: 11, fontWeight: 600, color: '#334155', fontFamily: 'Inter, system-ui, sans-serif' }}>
          {name}
        </div>
        <Handle type="source" id="gantt-src" position={Position.Right} style={{ opacity: 0 }} isConnectable={false} />
      </div>
    )
  }

  const pct = Math.max(0, Math.min(100, c?.progress ?? 0))
  const labelInside = w >= 64
  return (
    <div style={{ width: w, height: h, position: 'relative', overflow: 'visible' }}>
      <Handle type="target" id="gantt-tgt" position={Position.Left} style={{ opacity: 0 }} isConnectable={false} />
      <div style={{ width: '100%', height: '100%', background: st.bar, borderRadius: 5, overflow: 'hidden', position: 'relative', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
        {pct > 0 && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: st.fill, opacity: 0.85 }} />}
        {labelInside && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', padding: '0 8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11, fontWeight: 600, color: st.text, fontFamily: 'Inter, system-ui, sans-serif' }}>
            {name}
          </div>
        )}
      </div>
      {!labelInside && (
        <div style={{ position: 'absolute', left: w + 6, top: 0, height: h, display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', fontSize: 11, fontWeight: 600, color: '#334155', fontFamily: 'Inter, system-ui, sans-serif' }}>
          {name}
        </div>
      )}
      <Handle type="source" id="gantt-src" position={Position.Right} style={{ opacity: 0 }} isConnectable={false} />
    </div>
  )
}

export default memo(GanttTaskNode)
